import * as S from "../styles";
import {
  walking,
  driving,
  fastestRoute,
  noRoute,
  trySuggestion,
  calculateCommute,
  searchMap,
} from "../translations";
import {
  Select,
  Spacer,
  SVG,
  Text,
  LoadingItem,
  theme,
} from "@local-logic/core/ui";
import React, { useEffect, useMemo, useState } from "react";
import DrivingSVG from "~/assets/icons/Driving.svg";
import WalkingSVG from "~/assets/icons/Walking.svg";
import CloseSVG from "~/assets/icons/close.svg";
import AnimateHeight from "~/components/AnimateHeight";
import Search from "~/components/Search";
import { useStateManagerContext } from "~/context/stateManager";
import useIsMobile from "~/hooks/useIsMobile";
import { useLocalContent, type Location } from "~/sdks/LocalContent/context";
import {
  GoogleTravelMode,
  formatDestination,
  formatOrigin,
  useGoogleComputeRoute,
} from "~/sdks/LocalContent/features/useGoogleComputeRoute";
import { LocalContentProps } from "~/sdks/LocalContent/types";

export interface CommuteCalculatorProps {
  className?: string;
  onClose: () => void;
}

const GoogleCommuteCalculator = ({
  className,
  onClose,
}: CommuteCalculatorProps) => {
  const { sdkState } = useStateManagerContext<LocalContentProps>();
  const { destination, setDestination, setCommuteGeojson } = useLocalContent();
  const [transportMode, setTransportMode] =
    useState<keyof typeof transportModeMap>("walk");
  const isMobile = useIsMobile();

  const { data, isFetching, isFetched } = useGoogleComputeRoute({
    travelMode: transportMode.toUpperCase() as GoogleTravelMode,
    origin: formatOrigin(sdkState),
    destination: formatDestination(destination),
    units: "METRIC",
    polylineEncoding: "ENCODED_POLYLINE",
  });

  useEffect(() => {
    if (!data?.routes[0]) {
      return;
    }

    const decodedPolyline =
      data &&
      google.maps.geometry.encoding.decodePath(
        data?.routes[0].polyline.encodedPolyline
      );

    setCommuteGeojson(decodedPolyline);
  }, [data]);

  const transportModeMap = {
    walk: [walking, WalkingSVG],
    drive: [driving, DrivingSVG],
  };

  const duration = useMemo(
    () => data?.routes[0].localizedValues.duration.text,
    [data]
  );
  const distance = useMemo(
    () => data?.routes[0].localizedValues.distance.text,
    [data]
  );

  const getResults = () => {
    if (isFetching) {
      return <LoadingItem height="70.4px" width="100%" />;
    }

    if (data?.routes) {
      return (
        <S.CommuteInfo>
          <Text variant="h5">{duration}</Text>
          <Text>
            {distance} – {fastestRoute}
          </Text>
        </S.CommuteInfo>
      );
    }

    return (
      <S.CommuteInfo>
        <Text variant="h5">{noRoute}</Text>
        <Text>{trySuggestion}</Text>
      </S.CommuteInfo>
    );
  };

  return (
    <S.CommuteCalculatorContainer className={className}>
      <S.IconButton
        size="medium"
        color="neutral"
        onClick={onClose}
        SVGElement={CloseSVG as unknown as React.FC}
      />

      <S.CommuteSearch>
        <Search
          label={calculateCommute}
          placeholder={destination?.label || searchMap}
          renderListAbove={isMobile}
          renderListAbovePosition={30}
          onSearch={(e) => {
            setDestination({ lat: e.latitude, lng: e.longitude });
          }}
        />
        <S.SelectContainer>
          <Select.Root
            value={transportMode}
            onValueChange={(v: keyof typeof transportModeMap) => {
              setTransportMode(v);
            }}
          >
            <Select.Trigger>
              <SVG
                SVGElement={
                  transportModeMap[transportMode][1] as unknown as React.FC
                }
                fill={theme["--ll-color-on-background-variant1"]}
                width={16}
              />
              {transportModeMap[transportMode][0]}
              <Select.Content>
                <Select.Item value="drive">{driving}</Select.Item>
                <Select.Item value="walk">{walking}</Select.Item>
              </Select.Content>
            </Select.Trigger>
          </Select.Root>
        </S.SelectContainer>
      </S.CommuteSearch>
      {(isFetched || isFetching) && (
        <>
          <Spacer vertical spacing="--ll-spacing-x-small" />
          <AnimateHeight open={!!data}>{getResults()}</AnimateHeight>
        </>
      )}
    </S.CommuteCalculatorContainer>
  );
};

export default GoogleCommuteCalculator;
